import { zodResolver } from '@hookform/resolvers/zod';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  IconPicker,
  Input,
  ScrollArea,
  Textarea,
} from '@zond/ui';
import * as LucideIcons from 'lucide-react';
import { LucideProps } from 'lucide-react';
import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useCreateAlert, useDeleteAlert, useUpdateAlert } from '../alerts.queries';
import { useAlerts } from '../context/alerts-context';

// Get all available icons
const availableIcons = Object.keys(LucideIcons).filter(
  (name) => name !== 'createLucideIcon' && name !== 'default'
);

// Predefined alerts. `name`/`description`/`prompt` are the canonical English values
// submitted to and stored by the API; `templateKey` maps to the translated text
// shown in the selection cards.
const predefinedAlerts = [
  {
    templateKey: 'codeQuality',
    name: 'Code Quality Issues',
    description: 'Detects potential code quality problems and suggests improvements',
    icon: 'Code',
    prompt: 'Analyze the code for quality issues and provide detailed feedback on potential improvements.',
  },
  {
    templateKey: 'security',
    name: 'Security Vulnerabilities',
    description: 'Identifies security risks and provides mitigation strategies',
    icon: 'Shield',
    prompt: 'Check for security vulnerabilities and recommend specific fixes.',
  },
  {
    templateKey: 'performance',
    name: 'Performance Bottlenecks',
    description: 'Finds performance issues and optimization opportunities',
    icon: 'Gauge',
    prompt: 'Identify performance bottlenecks and suggest optimization strategies.',
  },
  {
    templateKey: 'documentation',
    name: 'Documentation Gaps',
    description: 'Highlights missing or incomplete documentation',
    icon: 'FileText',
    prompt: 'Review documentation coverage and highlight areas needing improvement.',
  },
  {
    templateKey: 'bestPractices',
    name: 'Best Practices',
    description: 'Ensures adherence to coding best practices and standards',
    icon: 'CheckCircle',
    prompt: 'Review code for adherence to best practices and suggest improvements.',
  },
];

interface AlertFormData {
  name: string;
  icon: string;
  prompt: string;
  description: string;
}

export function AlertsDialogs() {
  const { t } = useTranslation('alerts');
  const { isOpen, setOpen, selectedAlert } = useAlerts();
  const createAlert = useCreateAlert();
  const updateAlert = useUpdateAlert();
  const deleteAlert = useDeleteAlert();
  const [showCustomForm, setShowCustomForm] = useState(false);

  const form = useForm<AlertFormData>({
    defaultValues: {
      name: '',
      icon: availableIcons[Math.floor(Math.random() * availableIcons.length)],
      prompt: '',
      description: '',
    },
    resolver: zodResolver(
      z.object({
        name: z.string().min(1, t('validation.nameRequired')),
        icon: z.string().min(1, t('validation.iconRequired')),
        prompt: z.string().min(1, t('validation.promptRequired')),
        description: z.string().min(1, t('validation.descriptionRequired')),
      })
    ),
  });

  // Reset form when dialog opens/closes or selected alert changes
  useEffect(() => {
    if (isOpen === 'edit' && selectedAlert) {
      form.reset({
        name: selectedAlert.name,
        icon: selectedAlert.icon,
        prompt: selectedAlert.prompt,
        description: selectedAlert.description || '',
      });
    } else {
      form.reset({
        name: '',
        icon: availableIcons[Math.floor(Math.random() * availableIcons.length)],
        prompt: '',
        description: '',
      });
      setShowCustomForm(false);
    }
  }, [isOpen, selectedAlert, form]);

  const onSubmit = async (data: AlertFormData) => {
    try {
      if (isOpen === 'create') {
        await createAlert.mutateAsync({
          ...data,
          fromTemplate: false,
        });
      } else if (isOpen === 'edit' && selectedAlert) {
        await updateAlert.mutateAsync({
          id: selectedAlert.id,
          data: {
            ...data,
          },
        });
      }
      setOpen(null);
      form.reset();
    } catch (error) {
      console.error('Failed to save alert:', error);
    }
  };

  const handlePredefinedSelect = async (alert: (typeof predefinedAlerts)[0]) => {
    try {
      const { templateKey: _templateKey, ...alertData } = alert;
      await createAlert.mutateAsync({
        ...alertData,
        fromTemplate: true,
      });
      setOpen(null);
    } catch (error) {
      console.error('Failed to create predefined alert:', error);
    }
  };

  const handleDelete = async () => {
    if (selectedAlert) {
      try {
        await deleteAlert.mutateAsync(selectedAlert.id);
        setOpen(null);
      } catch (error) {
        console.error('Failed to delete alert:', error);
      }
    }
  };

  const CreateAlertContent = () => {
    if (showCustomForm) {
      return (
        <>
          <DialogHeader>
            <DialogTitle>{t('dialogs.createCustom.title')}</DialogTitle>
            <DialogDescription>{t('dialogs.createCustom.description')}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.name.label')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('form.name.placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="icon"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.icon.label')}</FormLabel>
                    <FormControl>
                      <div className="flex">
                        <IconPicker
                          value={field.value}
                          onChange={field.onChange}
                          placeholder={t('form.icon.placeholder')}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.description.label')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('form.description.placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="prompt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('form.prompt.label')}</FormLabel>
                    <FormControl>
                      <Textarea {...field} placeholder={t('form.prompt.placeholder')} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCustomForm(false)}>
                  {t('common:actions.back')}
                </Button>
                <Button type="submit">{t('dialogs.createCustom.submit')}</Button>
              </div>
            </form>
          </Form>
        </>
      );
    }

    return (
      <>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>{t('dialogs.create.title')}</DialogTitle>
              <DialogDescription>{t('dialogs.create.description')}</DialogDescription>
            </div>
            <Button onClick={() => setShowCustomForm(true)} variant="outline" size="sm">
              <Plus className="w-4 h-4" />
              {t('dialogs.create.customAlert')}
            </Button>
          </div>
        </DialogHeader>
        <ScrollArea className="h-[400px] pr-4">
          <div className="grid grid-cols-1 gap-3">
            {predefinedAlerts.map((alert, index) => {
              const Icon = LucideIcons[
                alert.icon as keyof typeof LucideIcons
              ] as React.ComponentType<LucideProps>;
              return (
                <Card
                  key={index}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handlePredefinedSelect(alert)}
                >
                  <CardHeader className="p-4">
                    <div className="flex gap-4">
                      <div className="rounded-lg bg-primary/10 p-2 h-[42px] w-[42px] flex items-center justify-center shrink-0">
                        {Icon && <Icon className="h-5 w-5 text-primary" />}
                      </div>
                      <div className="flex-1 flex flex-col justify-center min-h-[42px]">
                        <CardTitle className="text-base leading-tight">
                          {t(`templates.${alert.templateKey}.name`)}
                        </CardTitle>
                        <CardDescription className="mt-0.5 leading-tight">
                          {t(`templates.${alert.templateKey}.description`)}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      </>
    );
  };

  return (
    <>
      <Dialog
        open={isOpen === 'create' || isOpen === 'edit'}
        onOpenChange={(open) => {
          if (!open) {
            setOpen(null);
            setShowCustomForm(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl [&>button]:hidden">
          {isOpen === 'create' ? (
            <CreateAlertContent />
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t('dialogs.edit.title')}</DialogTitle>
                <DialogDescription>{t('dialogs.edit.description')}</DialogDescription>
              </DialogHeader>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.name.label')}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={t('form.name.placeholder')} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="icon"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.icon.label')}</FormLabel>
                        <FormControl>
                          <div className="flex">
                            <IconPicker
                              value={field.value}
                              onChange={field.onChange}
                              placeholder={t('form.icon.placeholder')}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.description.label')}</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder={t('form.description.placeholder')} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="prompt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('form.prompt.label')}</FormLabel>
                        <FormControl>
                          <Textarea {...field} placeholder={t('form.prompt.placeholder')} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setOpen(null)}>
                      {t('common:actions.cancel')}
                    </Button>
                    <Button type="submit">{t('common:actions.saveChanges')}</Button>
                  </div>
                </form>
              </Form>
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={isOpen === 'delete'} onOpenChange={() => setOpen(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('dialogs.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('dialogs.delete.description')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600">
              {t('common:actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
